import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatError } from '../contexts/AuthContext';
import {
  ChevronLeft, ThumbsUp, Bell, Eye, MessageSquare, Clock, Lock, Pin,
  Trash2, Flag, User, Shield, Edit2, CornerUpLeft, X
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import AttachmentPicker from '../components/AttachmentPicker';
import AttachmentDisplay from '../components/AttachmentDisplay';
import ShareButtons from '../components/ShareButtons';
import AdSlot from '../components/AdSlot';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import NotFoundPage from './NotFoundPage';
import RichTextEditor from '../components/RichTextEditor';
import RichContent, { stripHtml } from '../components/RichContent';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function getAvatarUrl(url) {
  if (!url) return '';
  return url.startsWith('http') ? url : `${API}/files/${url}`;
}

function AuthorInfo({ username, displayName, avatarUrl, date }) {
  const initials = (displayName || username || 'U').slice(0, 2).toUpperCase();
  return (
    <div className="flex items-center gap-3">
      <Avatar className="w-9 h-9">
        <AvatarImage src={getAvatarUrl(avatarUrl)} />
        <AvatarFallback className="bg-emerald-100 text-emerald-700 text-sm font-bold">{initials}</AvatarFallback>
      </Avatar>
      <div>
        <div className="font-semibold text-slate-800 text-sm">{displayName || username}</div>
        <div className="text-xs text-slate-400 flex items-center gap-1">
          <Clock className="w-3 h-3" />{formatDate(date)}
        </div>
      </div>
    </div>
  );
}

export default function TopicDetailPage() {
  const { topicId } = useParams();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [topic, setTopic] = useState(null);
  const [replies, setReplies] = useState([]);
  const [quotedReply, setQuotedReply] = useState(null); // {_id, author_display_name, content}
  const [userLiked, setUserLiked] = useState(false);
  const [userFollowed, setUserFollowed] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [showReport, setShowReport] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTopic = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/forum/topics/${topicId}`, { withCredentials: true });
      setTopic(r.data.topic);
      setReplies(r.data.replies || []);
      setUserLiked(r.data.user_liked);
      setUserFollowed(r.data.user_followed);
    } catch {
      setTopic(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTopic(); }, [topicId]);

  const handleLike = async () => {
    if (!user) { navigate('/giris'); return; }
    try {
      const r = await axios.post(`${API}/forum/topics/${topicId}/like`, {}, { withCredentials: true });
      setUserLiked(r.data.liked);
      setTopic(prev => ({ ...prev, like_count: prev.like_count + (r.data.liked ? 1 : -1) }));
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleFollow = async () => {
    if (!user) { navigate('/giris'); return; }
    try {
      const r = await axios.post(`${API}/forum/topics/${topicId}/follow`, {}, { withCredentials: true });
      setUserFollowed(r.data.following);
      toast.success(r.data.following ? 'Konu takibe alındı' : 'Konu takipten çıkarıldı');
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!stripHtml(replyContent).trim() && replyAttachments.length === 0) return;
    setSubmitting(true);
    try {
      const payload = { content: replyContent, attachments: replyAttachments };
      if (quotedReply) payload.reply_to = quotedReply._id;
      const r = await axios.post(`${API}/forum/topics/${topicId}/replies`, payload, { withCredentials: true });
      setReplies(prev => [...prev, r.data.reply]);
      setTopic(prev => ({ ...prev, reply_count: (prev.reply_count || 0) + 1 }));
      setReplyContent('');
      setReplyAttachments([]);
      setQuotedReply(null);
      toast.success('Yorumunuz eklendi!');
    } catch (err) { toast.error(formatError(err)); }
    finally { setSubmitting(false); }
  };

  const handleDeleteTopic = async () => {
    if (!window.confirm('Konuyu silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/forum/topics/${topicId}`, { withCredentials: true });
      toast.success('Konu silindi');
      navigate('/forum');
    } catch (err) { toast.error(formatError(err)); }
  };

  const openEditTopic = () => {
    setEditTitle(topic?.title || '');
    setEditContent(topic?.content || '');
    setEditOpen(true);
  };

  const handleEditTopic = async (e) => {
    e.preventDefault();
    if (!editTitle.trim() || !editContent.trim()) {
      toast.error('Başlık ve içerik boş olamaz');
      return;
    }
    setEditSaving(true);
    try {
      await axios.patch(`${API}/forum/topics/${topicId}`, { title: editTitle, content: editContent }, { withCredentials: true });
      setTopic(prev => ({ ...prev, title: editTitle, content: editContent }));
      setEditOpen(false);
      toast.success('Konu güncellendi');
    } catch (err) { toast.error(formatError(err)); }
    finally { setEditSaving(false); }
  };

  const handleDeleteReply = async (replyId) => {
    if (!window.confirm('Yorumu silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/forum/replies/${replyId}`, { withCredentials: true });
      setReplies(prev => prev.filter(r => r._id !== replyId));
      setTopic(prev => ({ ...prev, reply_count: Math.max(0, (prev.reply_count || 1) - 1) }));
      toast.success('Yorum silindi');
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleToggleLock = async () => {
    try {
      await axios.patch(`${API}/forum/topics/${topicId}`, { is_locked: !topic.is_locked }, { withCredentials: true });
      setTopic(prev => ({ ...prev, is_locked: !prev.is_locked }));
      toast.success(topic.is_locked ? 'Konu açıldı' : 'Konu kilitlendi');
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleReport = async () => {
    if (!reportReason.trim()) return;
    try {
      await axios.post(`${API}/forum/report`, {
        content_type: reportTarget?.type || 'topic',
        content_id: reportTarget?.id || topicId,
        reason: reportReason,
      }, { withCredentials: true });
      toast.success('Rapor gönderildi');
      setShowReport(false);
      setReportReason('');
    } catch (err) { toast.error(formatError(err)); }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="w-10 h-10 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!topic) return <NotFoundPage title="Konu bulunamadı" message="Aradığınız forum konusu silinmiş, taşınmış ya da adresi hatalı yazılmış olabilir." />;

  const canModerate = isAdmin || user?._id === topic.author_id;
  const isAuthor = user?._id === topic.author_id;

  // SEO meta derived from topic content
  const metaDesc = topic?.content ? stripHtml(topic.content, 160) : '';
  const topicUrl = topic ? `/forum/konu/${topic.slug || topic._id}` : '';
  const topicJsonLd = topic ? {
    '@context': 'https://schema.org',
    '@type': 'DiscussionForumPosting',
    headline: topic.title,
    articleSection: topic.category_name,
    author: { '@type': 'Person', name: topic.author_display_name || topic.author_username },
    datePublished: topic.created_at,
    dateModified: topic.last_reply_at || topic.created_at,
    interactionStatistic: [
      { '@type': 'InteractionCounter', interactionType: 'https://schema.org/CommentAction', userInteractionCount: topic.reply_count || 0 },
      { '@type': 'InteractionCounter', interactionType: 'https://schema.org/ViewAction', userInteractionCount: topic.views || 0 },
      { '@type': 'InteractionCounter', interactionType: 'https://schema.org/LikeAction', userInteractionCount: topic.like_count || 0 },
    ],
  } : null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {topic && (
        <SEO
          title={topic.title}
          description={metaDesc}
          type="article"
          canonicalPath={topicUrl}
          noindex={!!topic.noindex}
          jsonLd={topicJsonLd}
        />
      )}
      {topic && (
        <Breadcrumbs items={[
          { name: 'Forum', href: '/forum' },
          { name: topic.category_name || 'Kategori', href: `/forum/kategori/${topic.category_slug}` },
          { name: topic.title },
        ]} />
      )}
      <AdSlot placement="topic_top" className="mb-6" />
      {/* Topic */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {topic.is_pinned && <span className="text-xs bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded-full border border-yellow-200 flex items-center gap-1"><Pin className="w-3 h-3"/>Sabit</span>}
                {topic.is_locked && <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full border border-red-200 flex items-center gap-1"><Lock className="w-3 h-3"/>Kilitli</span>}
              </div>
              <h1 className="text-xl sm:text-2xl font-extrabold text-slate-800 break-words" style={{ fontFamily: 'Outfit, sans-serif' }}>{topic.title}</h1>
            </div>
            {canModerate && (
              <div className="flex items-center gap-1 sm:gap-2 sm:shrink-0 flex-wrap">
                {isAdmin && (
                  <Button variant="ghost" size="sm" onClick={handleToggleLock} data-testid="topic-lock-btn">
                    <Lock className="w-4 h-4 mr-1" />{topic.is_locked ? 'Aç' : 'Kilitle'}
                  </Button>
                )}
                {isAuthor && (
                  <Button variant="ghost" size="sm" onClick={openEditTopic} data-testid="topic-edit-btn">
                    <Edit2 className="w-4 h-4 mr-1" />Düzenle
                  </Button>
                )}
                <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleDeleteTopic} data-testid="topic-delete-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          <AuthorInfo username={topic.author_username} displayName={topic.author_display_name} avatarUrl={topic.author_avatar} date={topic.created_at} />

          <div className="mt-5" data-testid="topic-content">
            <RichContent html={topic.content} />
          </div>
          <AttachmentDisplay attachments={topic.attachments} testIdPrefix="topic-attach" />

          <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100 flex-wrap gap-y-3">
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <button
                onClick={handleLike}
                data-testid="topic-like-btn"
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  userLiked ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                <ThumbsUp className={`w-4 h-4 ${userLiked ? 'fill-current' : ''}`} />
                {topic.like_count || 0}
              </button>
              <button
                onClick={handleFollow}
                data-testid="topic-follow-btn"
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  userFollowed ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                <Bell className="w-4 h-4" />
                {userFollowed ? 'Takip Ediliyor' : 'Takip Et'}
              </button>
            </div>
            <div className="flex items-center gap-3 sm:gap-4 text-xs text-slate-400 flex-wrap">
              <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{topic.views || 0} görüntülenme</span>
              <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{topic.reply_count || 0} yanıt</span>
              {user && (
                <button
                  onClick={() => { setReportTarget({ type: 'topic', id: topicId }); setShowReport(true); }}
                  className="flex items-center gap-1 text-slate-400 hover:text-red-500 transition-colors"
                  data-testid="topic-report-btn"
                >
                  <Flag className="w-3 h-3" /> Şikayet
                </button>
              )}
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-3 flex-wrap">
            <span className="text-xs font-semibold text-slate-500">Paylaş:</span>
            <ShareButtons title={topic.title} text={topic.title} />
          </div>
        </div>
      </div>

      {/* Replies */}
      {replies.length > 0 && (
        <div className="space-y-4 mb-6">
          <h2 className="font-bold text-slate-700 text-lg">{replies.length} Yanıt</h2>
          {replies.map((reply, i) => (
            <div key={reply._id} data-testid={`reply-${reply._id}`} className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="flex items-start justify-between">
                <AuthorInfo username={reply.author_username} displayName={reply.author_display_name} avatarUrl={reply.author_avatar} date={reply.created_at} />
                <div className="flex items-center gap-2">
                  {user && (
                    <button
                      onClick={() => {
                        setQuotedReply({ _id: reply._id, author_display_name: reply.author_display_name, content: reply.content });
                        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                      }}
                      className="text-slate-400 hover:text-emerald-600 transition-colors text-xs flex items-center gap-1"
                      data-testid={`reply-quote-btn-${reply._id}`}
                      title="Alıntıla"
                    >
                      <CornerUpLeft className="w-3.5 h-3.5" /> Alıntıla
                    </button>
                  )}
                  {user && (
                    <button
                      onClick={() => { setReportTarget({ type: 'reply', id: reply._id }); setShowReport(true); }}
                      className="text-slate-300 hover:text-red-400 transition-colors"
                      data-testid={`reply-report-btn-${reply._id}`}
                    >
                      <Flag className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {(isAdmin || user?._id === reply.author_id) && (
                    <button
                      onClick={() => handleDeleteReply(reply._id)}
                      className="text-slate-300 hover:text-red-500 transition-colors"
                      data-testid={`reply-delete-btn-${reply._id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              {reply.reply_to && (
                <div className="mt-3 rounded-lg px-3 py-2 bg-emerald-50 border-l-4 border-emerald-500 text-sm" data-testid={`reply-quote-${reply._id}`}>
                  <div className="text-[11px] font-semibold text-emerald-700">{reply.reply_to.author_display_name || 'Yorum'} alıntıladı</div>
                  <div className="text-slate-600 line-clamp-2">{stripHtml(reply.reply_to.content)}</div>
                </div>
              )}
              <div className="mt-4" data-testid={`reply-content-${reply._id}`}>
                <RichContent html={reply.content} />
              </div>
              <AttachmentDisplay attachments={reply.attachments} testIdPrefix={`reply-attach-${reply._id}`} />
              <div className="text-xs text-slate-400 mt-3">#{i + 1}</div>
            </div>
          ))}
        </div>
      )}

      {/* Reply form */}
      {user && !topic.is_locked ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-bold text-slate-800 mb-4">Yanıt Yaz</h3>
          <form onSubmit={handleReply}>
            {quotedReply && (
              <div className="mb-3 flex items-start gap-2 bg-emerald-50 border-l-4 border-emerald-500 rounded-r-lg px-3 py-2" data-testid="forum-quote-preview">
                <CornerUpLeft className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-semibold text-emerald-700">{quotedReply.author_display_name} alıntılanıyor</div>
                  <div className="text-xs text-slate-600 line-clamp-2">{stripHtml(quotedReply.content)}</div>
                </div>
                <button type="button" onClick={() => setQuotedReply(null)} className="text-slate-400 hover:text-red-500 shrink-0" data-testid="forum-quote-cancel">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className="mb-3" data-testid="reply-textarea">
              <RichTextEditor
                value={replyContent}
                onChange={setReplyContent}
                placeholder="Yanıtınızı yazın..."
                minHeight={140}
              />
            </div>
            <div className="mb-3">
              <AttachmentPicker attachments={replyAttachments} setAttachments={setReplyAttachments} testIdPrefix="reply-attach-picker" />
            </div>
            <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="reply-submit-btn">
              {submitting ? 'Gönderiliyor...' : 'Yanıt Gönder'}
            </Button>
          </form>
        </div>
      ) : !user ? (
        <div className="text-center py-8 bg-white rounded-2xl border border-slate-200">
          <p className="text-slate-500 mb-3">Yanıt yazmak için giriş yapmanız gerekiyor</p>
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => navigate('/giris')}>Giriş Yap</Button>
        </div>
      ) : (
        <div className="text-center py-6 bg-slate-50 rounded-2xl border border-slate-200">
          <Lock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500">Bu konu kilitli, yanıt yazılamaz</p>
        </div>
      )}

      {/* Report dialog */}
      <Dialog open={showReport} onOpenChange={setShowReport}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>İçeriği Şikayet Et</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Textarea
              value={reportReason}
              onChange={e => setReportReason(e.target.value)}
              placeholder="Şikayet nedeninizi yazın..."
              rows={3}
              className="resize-none"
              data-testid="report-reason-input"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowReport(false)}>İptal</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleReport} data-testid="report-submit-btn">Şikayet Et</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Topic Dialog (author only) */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[560px]" data-testid="edit-topic-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Konuyu Düzenle</DialogTitle>
            <DialogDescription>Başlığı ve içeriği güncelleyebilirsin.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditTopic} className="space-y-4 mt-2">
            <div>
              <Label htmlFor="edit-topic-title">Başlık</Label>
              <Input id="edit-topic-title" value={editTitle} onChange={e => setEditTitle(e.target.value)} className="mt-1" data-testid="edit-topic-title" />
            </div>
            <div>
              <Label htmlFor="edit-topic-content">İçerik</Label>
              <div className="mt-1" data-testid="edit-topic-content">
                <RichTextEditor
                  value={editContent}
                  onChange={setEditContent}
                  placeholder="Konu içeriği..."
                  minHeight={220}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)} data-testid="edit-topic-cancel">İptal</Button>
              <Button type="submit" disabled={editSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="edit-topic-submit">
                {editSaving ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
